/**
 * Worker-agnostic transcript entry types.
 *
 * Mirrors the Python `flow_sdk/transcript_analyzer/entries/*.py` types.
 * The server's `/api/v1/transcripts/{worker_type}` endpoint returns
 * `entry.to_dict()` per entry, and these types describe the JSON shape.
 *
 * Discriminated by the `kind` field. Use the `is*` guards in this file or
 * a `switch (entry.kind)` block to narrow types.
 */

/**
 * The transcript file format the backend parsed, and where it came from.
 * Mirrors `TranscriptFormat` / `TranscriptSource` in
 * `flow_sdk/transcript_analyzer/transcript.py`.
 */
export enum TranscriptFormat {
  CLAUDE_JSONL = 'claude_jsonl',
  CODEX_STREAM = 'codex_stream',
  CODEX_ROLLOUT = 'codex_rollout',
  COPILOT_STREAM = 'copilot_stream',
  COPILOT_EVENTS = 'copilot_events',
  // FlowPad owns both opencode formats: the headless stdout tee, and the
  // projection assembled from opencode's SQLite store for PTY sessions.
  OPENCODE_STREAM = 'opencode_stream',
  OPENCODE_SESSION = 'opencode_session',
}

export enum TranscriptSource {
  PROCESS_LOCAL = 'process_local',
  WORKER_SESSION = 'worker_session',
}

export type EntryKind =
  | 'user_message'
  | 'assistant_message'
  | 'tool_use'
  | 'tool_result'
  | 'file_write'
  | 'file_edit'
  | 'file_read'
  | 'shell_command'
  | 'flow_command'
  | 'search'
  | 'web_fetch'
  | 'todo_update'
  | 'agent_spawn'
  | 'skill_call'
  | 'artifact'
  | 'compaction'
  | 'system'
  | 'summary'
  | 'meta'
  | 'token_usage'
  | 'worker_unavailable'
  | 'unknown';

/**
 * Common envelope fields shared across every entry.
 *
 * Pinned field-for-field against Python `TranscriptEntry.to_dict()` by
 * `ui/tests/unit/transcript-entry-envelope-contract.test.ts` — this interface
 * silently drifted twice before that test existed.
 */
export interface BaseEntry {
  kind: EntryKind;
  id: string;
  session_id: string;
  timestamp: string;
  worker: string;
  parent_id: string | null;
  is_sidechain: boolean;
  /** Worker-side stable id (codex `response_item.id`, claude `message.id`). */
  entry_id: string | null;
  /** Model name (claude `message.model`, codex `turn_context.model`). */
  model: string | null;
  /** The skill this line is attributed to (Claude `attributionSkill`). */
  attribution_skill: string | null;
  /**
   * Generated by the backend's DERIVATION LAYER (`transcript_analyzer/derivation/`)
   * rather than parsed from a vendor line. Derivation is ADDITIVE: a virtual
   * entry is appended BESIDE its source, not in place of it.
   */
  virtual: boolean;
  /**
   * The id of the entry this was derived FROM, when virtual. `virtual` says
   * "generated"; this says "from what" — which is what lets a consumer suppress
   * a source whose refinement is present with no per-kind rules. Chains exist
   * (shell_command → flow_command → artifact), so only a chain's LEAF has no
   * descendant pointing at it.
   */
  derived_from: string | null;
}

export interface UserMessageEntry extends BaseEntry {
  kind: 'user_message';
  text: string;
  role: string; // "user" by default; non-user roles route to SystemEntry upstream
  /**
   * Framework-injected user line — a skill body, a slash-command expansion, a
   * Flowpad agent wrapper. The server has always sent this (`UserMessage.to_dict`
   * in flow_sdk/transcript_analyzer); mirroring it lets surfaces tell "what the
   * human sent" from "what the harness fed the model" without re-sniffing text.
   * Consumed by `promptDisplayText`, which collapses the expanded SKILL.md row.
   */
  is_meta?: boolean;
}

export interface AssistantMessageEntry extends BaseEntry {
  kind: 'assistant_message';
  text: string;
  thinking: string | null;
  /** Codex-only: `final_answer` | `commentary`. Null for claude. */
  phase: string | null;
}

export interface ToolUseEntry extends BaseEntry {
  kind: 'tool_use';
  tool_name: string;
  tool_use_id: string;
  /** Decoded JSON arguments object (string args are JSON-decoded server-side). */
  tool_input: Record<string, unknown>;
}

export interface ToolResultEntry extends BaseEntry {
  kind: 'tool_result';
  tool_use_id: string;
  tool_output: string;
  is_error: boolean;
  file_path: string | null;
  /** Cross-referenced from the matching ToolUse via `call_id`. May be null. */
  tool_name: string | null;
  /** Wall time of the tool execution in milliseconds (when known). */
  duration_ms: number | null;
  /** Process exit code (when applicable). */
  exit_code: number | null;
  /** Codex-only: ``Original token count`` from the output preamble. */
  output_token_count: number | null;
}

export interface SystemEntry extends BaseEntry {
  kind: 'system';
  subtype: string;
  payload: Record<string, unknown>;
}

export interface SummaryEntry extends BaseEntry {
  kind: 'summary';
  summary_text: string;
}

export interface MetaEntry extends BaseEntry {
  kind: 'meta';
  meta_kind: string;
  payload: Record<string, unknown>;
}

export interface TokenUsageEntry extends BaseEntry {
  kind: 'token_usage';
  /** USD cost of this stream, priced by the backend's tables. */
  cost_usd?: number;
  // New per-dim shape (post pricing-refactor). One entry per chargeable stream.
  count?: number;
  io?: 'input' | 'output';
  unit?: 'token' | 'request';
  cache?: 'none' | 'read' | 'write';
  cache_tier?: 'none' | '5m' | '1h';
  reasoning?: boolean;
  tool?: string | null;
  /** Codex-only cumulative carriers (the per-dim siblings carry chargeable counts). */
  total_input_tokens?: number | null;
  total_output_tokens?: number | null;
  turn_id?: string | null;
  // Legacy aggregate shape — still emitted by older Python servers that
  // haven't reloaded the pricing-refactor parser. The lens-viewer's
  // group-entries.ts collapses either shape into UnifiedEntry.usage so
  // the UI works during the rollout window.
  input_tokens?: number | null;
  output_tokens?: number | null;
  cached_input_tokens?: number | null;
  cache_read_tokens?: number | null;
  cache_creation_tokens?: number | null;
  reasoning_output_tokens?: number | null;
}

/** A worker cannot continue this chat, but another configured worker may. */
export type WorkerUnavailableReason = 'quota_exhausted' | 'rate_limited';

export interface WorkerUnavailableEntry extends BaseEntry {
  kind: 'worker_unavailable';
  reason: WorkerUnavailableReason;
  worker_type: string;
  provider_error: string;
  status_code: number | null;
  recoverable_with_alternative: boolean;
  message: string;
}

export interface UnknownEntry extends BaseEntry {
  kind: 'unknown';
  raw_data: Record<string, unknown>;
}

// ── Semantic operation entries (worker-agnostic) ───────────────────────────
//
// Parsers fold worker-specific tool names onto these kinds so renderers
// never sniff `tool_input.file_path` vs `tool_input.cmd` to figure out
// what the agent did. ToolUseEntry remains the catch-all bucket for tools
// that don't have a dedicated kind yet (MCP, bespoke).

export interface FileWriteEntry extends BaseEntry {
  kind: 'file_write';
  path: string;
  content: string | null;
  bytes_count: number | null;
  line_count: number | null;
  is_new: boolean;
  tool_name: string;
  tool_use_id: string;
}

export interface FileEditEntry extends BaseEntry {
  kind: 'file_edit';
  path: string;
  hunks: Array<{ old?: string; new?: string; replace_all?: boolean; header?: string; lines?: string[] }>;
  change_summary: string | null;
  tool_name: string;
  tool_use_id: string;
}

export interface FileReadEntry extends BaseEntry {
  kind: 'file_read';
  path: string;
  start_line: number | null;
  end_line: number | null;
  bytes_count: number | null;
  content_preview: string | null;
  tool_name: string;
  tool_use_id: string;
}

export interface ShellCommandEntry extends BaseEntry {
  kind: 'shell_command';
  command: string;
  cwd: string | null;
  exit_code: number | null;
  stdout_preview: string | null;
  stderr_preview: string | null;
  duration_ms: number | null;
  timeout: number | null;
  tool_name: string;
  tool_use_id: string;
}

/**
 * A `flow` CLI invocation — DERIVED from a shell command, never parsed off a
 * raw line. Mirrors backend `flow_sdk/transcript_analyzer/entries/flow_command.py`.
 * Extends the shell shape, so every shell field is still present.
 */
export interface FlowCommandEntry extends Omit<ShellCommandEntry, 'kind'> {
  kind: 'flow_command';
  /** Top-level CLI command: 'show', 'navigate', 'record', … */
  verb: string;
  /** Sub-command that addresses something: 'entity', 'file', … */
  subverb: string | null;
  /** The addressed TypeId or path, when the sub-command takes one. */
  target: string | null;
  flow_args: string[];
}

/**
 * The agent registering a deliverable — one refinement ABOVE `flow_command`
 * (`shell_command → flow_command → artifact`). Mirrors backend
 * `flow_sdk/transcript_analyzer/entries/artifact.py`.
 *
 * It deliberately carries no artifact id: identity is minted server-side after
 * the command ran and is not in the transcript. This is the ADDRESS the agent
 * used; `proc.artifacts` carries the identities.
 */
export interface ArtifactEntry extends Omit<FlowCommandEntry, 'kind'> {
  kind: 'artifact';
}

/**
 * A context compaction / summarization boundary (checkpoint + resume). Mirrors
 * backend `flow_sdk/transcript_analyzer/entries/compaction.py`. Claude only
 * today (codex `compacted`, copilot none).
 */
export interface CompactionEntry extends BaseEntry {
  kind: 'compaction';
  /** 'auto' | 'manual' (a `/compact` slash command). */
  trigger: string;
  summary_preview: string;
  pre_tokens: number | null;
}

export interface SearchEntry extends BaseEntry {
  kind: 'search';
  search_kind: string; // 'glob' | 'grep' | 'find'
  query: string;
  path: string | null;
  match_count: number | null;
  results_preview: string | null;
  tool_name: string;
  tool_use_id: string;
}

export interface WebFetchEntry extends BaseEntry {
  kind: 'web_fetch';
  url: string | null;
  query: string | null;
  prompt: string | null;
  bytes_count: number | null;
  status_code: number | null;
  result_preview: string | null;
  tool_name: string;
  tool_use_id: string;
}

export interface TodoUpdateEntry extends BaseEntry {
  kind: 'todo_update';
  items: Array<Record<string, unknown>>;
  tool_name: string;
  tool_use_id: string;
}

export interface AgentSpawnEntry extends BaseEntry {
  kind: 'agent_spawn';
  agent_type: string;
  prompt: string | null;
  description: string | null;
  tool_name: string;
  tool_use_id: string;
  /**
   * Absolute path to the spawned agent's own transcript JSONL, present only for
   * workflow-run spawns whose child transcript exists on disk (stamped by the
   * transcripts route). Drives the "open sub-agent transcript" affordance.
   */
  child_transcript_path?: string | null;
}

/**
 * A skill invocation, normalized across workers (Claude/Copilot native Skill
 * tool, Codex SKILL.md file-load). Mirrors backend
 * `flow_sdk/transcript_analyzer/entries/skill_call.py`.
 */
export interface SkillCallEntry extends BaseEntry {
  kind: 'skill_call';
  skill_name: string;
  /** 'tool' (native Skill tool) | 'file_load' (SKILL.md read). */
  invocation_kind: string;
  tool_name: string;
  tool_use_id: string;
}

export type GenericEntry =
  | UserMessageEntry
  | AssistantMessageEntry
  | ToolUseEntry
  | ToolResultEntry
  | FileWriteEntry
  | FileEditEntry
  | FileReadEntry
  | ShellCommandEntry
  | FlowCommandEntry
  | ArtifactEntry
  | CompactionEntry
  | SearchEntry
  | WebFetchEntry
  | TodoUpdateEntry
  | AgentSpawnEntry
  | SkillCallEntry
  | SystemEntry
  | SummaryEntry
  | MetaEntry
  | TokenUsageEntry
  | WorkerUnavailableEntry
  | UnknownEntry;

// ── Type guards ─────────────────────────────────────────────────────────────

export const isUserMessage = (e: GenericEntry): e is UserMessageEntry => e.kind === 'user_message';
export const isAssistantMessage = (e: GenericEntry): e is AssistantMessageEntry => e.kind === 'assistant_message';
export const isToolUse = (e: GenericEntry): e is ToolUseEntry => e.kind === 'tool_use';
export const isToolResult = (e: GenericEntry): e is ToolResultEntry => e.kind === 'tool_result';
export const isFileWrite = (e: GenericEntry): e is FileWriteEntry => e.kind === 'file_write';
export const isFileEdit = (e: GenericEntry): e is FileEditEntry => e.kind === 'file_edit';
export const isFileRead = (e: GenericEntry): e is FileReadEntry => e.kind === 'file_read';
export const isShellCommand = (e: GenericEntry): e is ShellCommandEntry => e.kind === 'shell_command';
export const isFlowCommand = (e: GenericEntry): e is FlowCommandEntry => e.kind === 'flow_command';
export const isArtifact = (e: GenericEntry): e is ArtifactEntry => e.kind === 'artifact';
export const isCompaction = (e: GenericEntry): e is CompactionEntry => e.kind === 'compaction';
export const isSearch = (e: GenericEntry): e is SearchEntry => e.kind === 'search';
export const isWebFetch = (e: GenericEntry): e is WebFetchEntry => e.kind === 'web_fetch';
export const isTodoUpdate = (e: GenericEntry): e is TodoUpdateEntry => e.kind === 'todo_update';
export const isAgentSpawn = (e: GenericEntry): e is AgentSpawnEntry => e.kind === 'agent_spawn';
export const isSkillCall = (e: GenericEntry): e is SkillCallEntry => e.kind === 'skill_call';
export const isWorkerUnavailable = (e: GenericEntry): e is WorkerUnavailableEntry =>
  e.kind === 'worker_unavailable';
export const isSystem = (e: GenericEntry): e is SystemEntry => e.kind === 'system';
export const isSummary = (e: GenericEntry): e is SummaryEntry => e.kind === 'summary';
export const isMeta = (e: GenericEntry): e is MetaEntry => e.kind === 'meta';
export const isTokenUsage = (e: GenericEntry): e is TokenUsageEntry => e.kind === 'token_usage';
export const isUnknown = (e: GenericEntry): e is UnknownEntry => e.kind === 'unknown';

/** True for any kind that represents a discrete agent operation (file/shell/web/etc). */
export const isOperation = (e: GenericEntry): boolean =>
  e.kind === 'file_write' || e.kind === 'file_edit' || e.kind === 'file_read' ||
  e.kind === 'shell_command' || e.kind === 'flow_command' || e.kind === 'artifact' ||
  e.kind === 'search' || e.kind === 'web_fetch' ||
  e.kind === 'todo_update' || e.kind === 'agent_spawn' ||
  e.kind === 'skill_call' || e.kind === 'tool_use';

// ── Header / response shape ─────────────────────────────────────────────────

export interface TranscriptHeader {
  /** Generic worker-session display title (same value the history list shows);
   *  used to label the transcript tab. Absent when nothing names the session. */
  name?: string;
  cwd?: string;
  cli_version?: string;
  originator?: string;
  model_provider?: string;
  git?: {
    branch?: string;
    commit_hash?: string;
    repository_url?: string;
  };
}

export interface ParsedTranscript {
  worker_type: string;
  session_id: string;
  path: string;
  /** True when this transcript arrived via a shared message (lives only under
   *  the instance's received_transcripts/ store) and therefore cannot be
   *  resumed locally — the viewer hides the resume affordance and offers an
   *  analyze-transcript worker instead. */
  received: boolean;
  transcript_format: TranscriptFormat | null;
  transcript_source: TranscriptSource | null;
  header: TranscriptHeader;
  entries: GenericEntry[];
}
